/**
 * Build Root-only CW/CCW preview for the IWM Root mapping dialog.
 * Flip mode remaps Root bug types in WPS/PQR reports; external passes are ignored in the UI.
 */

export type WeldBugType = 'CW' | 'CCW'

export interface PassBugTypeRow {
  pass: string
  bugTypes: WeldBugType[]
  isRoot: boolean
}

export interface IwmRootMappingPreview {
  /** Root passes only (internal / IWM). */
  rows: PassBugTypeRow[]
  /** Aggregated Root directions across Root pass labels. */
  rootBugTypes: WeldBugType[]
  rootHasDirection: boolean
  canFlipRoot: boolean
  disableReason: string | null
  /** Root before → after when flip is on */
  rootFlipLines: string[]
}

/**
 * IWM Root only — not Hot/Fill/Cap, not Semi-Automatic Root Misfire.
 * Matches: Root, Root 1, Root (CW), Root (CCW).
 */
export function isRootPassName(passName: string | null | undefined): boolean {
  const pass = (passName ?? '').trim().toLowerCase()
  if (!pass) return false
  // Semi-auto Root Misfire is a different workflow — never IWM Root flip.
  if (pass.includes('misfire')) return false
  return pass === 'root' || /^root(\s+\d+)?(\s*\(|$)/i.test(pass)
}

export function oppositeBugType(bugType: WeldBugType): WeldBugType {
  return bugType === 'CW' ? 'CCW' : 'CW'
}

/** Parse CW/CCW from ALX-style pass labels like `Root (CW)`. */
export function bugTypesFromAlxPassName(passName: string): WeldBugType[] {
  const out: WeldBugType[] = []
  if (/\(\s*CCW\s*\)/i.test(passName)) out.push('CCW')
  else if (/\(\s*CW\s*\)/i.test(passName)) out.push('CW')
  return out
}

/** Merge telemetry + ALX names, then keep only Root passes. */
export function mergeRootBugTypeRows(
  fromTelemetry: Array<{ pass: string; bugTypes: Array<'CW' | 'CCW'> }>,
  alxPassNames: string[]
): PassBugTypeRow[] {
  const byPass = new Map<string, Set<WeldBugType>>()

  for (const row of fromTelemetry) {
    const pass = row.pass.trim()
    if (!pass || !isRootPassName(pass)) continue
    const set = byPass.get(pass) ?? new Set<WeldBugType>()
    for (const b of row.bugTypes) {
      if (b === 'CW' || b === 'CCW') set.add(b)
    }
    byPass.set(pass, set)
  }

  for (const name of alxPassNames) {
    const pass = name.trim()
    if (!pass || !isRootPassName(pass)) continue
    const inferred = bugTypesFromAlxPassName(pass)
    const set = byPass.get(pass) ?? new Set<WeldBugType>()
    for (const b of inferred) set.add(b)
    byPass.set(pass, set)
  }

  return Array.from(byPass.entries())
    .map(([pass, set]) => ({
      pass,
      bugTypes: (['CW', 'CCW'] as const).filter((b) => set.has(b)),
      isRoot: true
    }))
    .sort((a, b) => a.pass.localeCompare(b.pass))
}

/** @deprecated Use mergeRootBugTypeRows — kept name for call-site clarity during rename. */
export function mergePassBugTypeRows(
  fromTelemetry: Array<{ pass: string; bugTypes: Array<'CW' | 'CCW'> }>,
  alxPassNames: string[]
): PassBugTypeRow[] {
  return mergeRootBugTypeRows(fromTelemetry, alxPassNames)
}

export function buildIwmRootMappingPreview(rows: PassBugTypeRow[]): IwmRootMappingPreview {
  const rootRows = rows.filter((r) => r.isRoot)
  const rootTypes = new Set<WeldBugType>()
  for (const r of rootRows) {
    for (const b of r.bugTypes) rootTypes.add(b)
  }
  const rootBugTypes = (['CW', 'CCW'] as const).filter((b) => rootTypes.has(b))
  const rootHasDirection = rootBugTypes.length > 0
  const canFlipRoot = rootHasDirection
  const disableReason = canFlipRoot
    ? null
    : 'No Root CW/CCW data on this weld. Keep As logged.'

  const rootFlipLines: string[] = []
  if (rootTypes.has('CW')) rootFlipLines.push('Root CW → CCW in WPS/PQR')
  if (rootTypes.has('CCW')) rootFlipLines.push('Root CCW → CW in WPS/PQR')
  if (rootFlipLines.length === 0) {
    rootFlipLines.push('No Root CW/CCW data to swap')
  }

  return {
    rows: rootRows,
    rootBugTypes,
    rootHasDirection,
    canFlipRoot,
    disableReason,
    rootFlipLines
  }
}
